import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, FloppyDisk, PaperPlaneTilt, UploadSimple, Trash, FileText, DownloadSimple, PencilSimple, Sparkle, CircleNotch, UserCircle, GraduationCap, CurrencyDollar, MapPin, Files, ShieldCheck, CheckCircle, XCircle, ChatCircleDots } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PhoneInput from '@/components/PhoneInput';
import FillDeclarationDialog from '@/components/FillDeclarationDialog';
import { COUNTRIES } from '@/utils/countries';

import { API } from '@/config/api';
const EXAM_TYPES = ['CLBPT', 'ACCUPLACER', 'iTEP'];
const DOC_TYPES = [
  'passport', 'home_country_passport', 'canadian_passport', 'canadian_passport_or_citizenship',
  'citizenship', 'citizenship_card', 'canadian_citizenship',
  'pr_card', 'driving_license',
  'driving_license_or_bcid', 'bcid', 'provincial_id',
  'photo', 'class_12_certificate', 'class_12_marksheet',
  'english_test_certificate', 'english_test_proof', 'canadian_transcript_2yr',
  'canadian_2yr_diploma', 'birth_certificate',
  'sin_document', 'notice_of_decision', 'canadian_id_proof',
  'student_declaration_form', 'other',
];
const DOC_TYPE_LABELS = {
  passport: 'Passport',
  home_country_passport: 'Home Country Passport',
  canadian_passport: 'Canadian Passport',
  canadian_passport_or_citizenship: 'Canadian Passport / Citizenship Certificate / Birth Certificate',
  citizenship: 'Canadian Citizenship Certificate',
  citizenship_card: 'Canadian Citizenship Card',
  canadian_citizenship: 'Canadian Citizenship Certificate',
  birth_certificate: 'Birth Certificate',
  pr_card: 'PR Card',
  driving_license: 'Driving License',
  driving_license_or_bcid: 'Driving License / BCID',
  bcid: 'BCID',
  provincial_id: 'Provincial Photo ID',
  photo: 'Photo',
  class_12_certificate: 'Class 12 Certificate',
  class_12_marksheet: 'Class 12 Marksheet / Proof',
  english_test_certificate: 'English Test Certificate',
  english_test_proof: 'English Test Proof',
  canadian_transcript_2yr: 'Canadian 2-Year Transcript',
  canadian_2yr_diploma: '2-Year Canadian Diploma Proof',
  sin_document: 'SIN Document',
  notice_of_decision: 'Notice of Decision',
  canadian_id_proof: 'Canadian ID Proof',
  student_declaration_form: 'Student Declaration Form',
  other: 'Other Document',
};

// The 4 status-in-Canada choices — everything else on the form pivots off this.
const STATUS_OPTIONS = [
  { value: 'pr', label: 'Permanent Resident', hint: 'Has a PR card', tone: 'emerald' },
  { value: 'citizen', label: 'Canadian Citizen', hint: 'Holds Canadian passport', tone: 'blue' },
  { value: 'refugee', label: 'Refugee / Protected Person', hint: 'Notice of Decision issued', tone: 'violet' },
];

// Compute the program end date the SAME way the backend does (Friday
// immediately before the first Monday of the same intake month next year).
// Purely a UX hint — the backend is still the source of truth at EA time.
function computeProgramEndDate(intakeDateIso) {
  if (!intakeDateIso) return '';
  const parts = intakeDateIso.slice(0, 10).split('-');
  if (parts.length !== 3) return '';
  const [y, m, dnum] = parts.map(Number);
  if (!y || !m || !dnum) return '';
  const monthIdx = m - 1;
  // Jan intake is an edge case — same-year Dec last Friday, otherwise 1yr
  // diplomas would optically look like 2yr programs.
  if (monthIdx === 0) {
    const last = new Date(y, 12, 0); // last day of Dec
    const dow = last.getDay(); // 0=Sun ... 5=Fri
    const back = (dow + 2) % 7; // days back to Friday
    last.setDate(last.getDate() - back);
    return last.toISOString().slice(0, 10);
  }
  // First Monday of the same month next year.
  const d = new Date(y + 1, monthIdx, 1);
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1); // 1 = Monday
  d.setDate(d.getDate() - 3); // Monday − 3 days = the preceding Friday
  return d.toISOString().slice(0, 10);
}

function formatFriendlyDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Dynamic mandatory document slots based on status in Canada.
// Doc-slot configuration per (status × mature-student) matrix.
//   - Not-mature keeps the existing rules.
//   - Mature swaps in a leaner list because Class 12 is no longer a
//     graduation gate; the college accepts the applicant on the basis of
//     age + Canadian residency + (optionally) a Canadian 2-year diploma.
function slotsForStatus(status, mature = false) {
  switch (status) {
    case 'pr':
      if (mature) {
        return [
          { key: 'pr_card', label: 'PR Card', doc_type: 'pr_card', matches: ['pr_card'] },
          { key: 'driving_license_or_bcid', label: 'Driving License / BCID', doc_type: 'driving_license_or_bcid', matches: ['driving_license_or_bcid', 'driving_license', 'bcid', 'provincial_id'] },
        ];
      }
      return [
        { key: 'class_12_marksheet', label: 'Class 12 Proof', doc_type: 'class_12_marksheet', matches: ['class_12_marksheet', 'class_12_certificate'] },
        { key: 'pr_card', label: 'PR Card', doc_type: 'pr_card', matches: ['pr_card'] },
        { key: 'driving_license_or_bcid', label: 'Driving License / BCID', doc_type: 'driving_license_or_bcid', matches: ['driving_license_or_bcid', 'driving_license', 'bcid', 'provincial_id'] },
        { key: 'home_country_passport', label: 'Home Country Passport', doc_type: 'home_country_passport', matches: ['home_country_passport', 'passport'] },
      ];
    case 'citizen':
      if (mature) {
        return [
          // Canadian Passport OR Birth Certificate OR Citizenship Certificate
          // — a single slot that accepts any of these Canadian-ID docs so
          // the recruiter can upload whichever the student has on hand.
          { key: 'canadian_id', label: 'Canadian Passport / Birth Certificate / Citizenship Certificate', doc_type: 'canadian_passport_or_citizenship', matches: ['canadian_passport_or_citizenship', 'canadian_passport', 'passport', 'birth_certificate', 'citizenship', 'citizenship_card', 'canadian_citizenship'] },
          { key: 'driving_license_or_bcid', label: 'Driving License / BCID', doc_type: 'driving_license_or_bcid', matches: ['driving_license_or_bcid', 'driving_license', 'bcid', 'provincial_id'] },
        ];
      }
      return [
        { key: 'class_12_marksheet', label: 'Class 12 Proof', doc_type: 'class_12_marksheet', matches: ['class_12_marksheet', 'class_12_certificate'] },
        { key: 'driving_license_or_bcid', label: 'Driving License / BCID', doc_type: 'driving_license_or_bcid', matches: ['driving_license_or_bcid', 'driving_license', 'bcid', 'provincial_id'] },
        { key: 'canadian_passport_or_citizenship', label: 'Canadian Passport / Canadian Citizenship Certificate / Canadian Birth Certificate', doc_type: 'canadian_passport_or_citizenship', matches: ['canadian_passport_or_citizenship', 'canadian_passport', 'passport', 'citizenship', 'citizenship_card', 'canadian_citizenship', 'birth_certificate', 'canadian_birth_certificate'] },
      ];
    case 'refugee':
      return [
        { key: 'sin_document', label: 'SIN Document', doc_type: 'sin_document', matches: ['sin_document'] },
        { key: 'notice_of_decision', label: 'Notice of Decision', doc_type: 'notice_of_decision', matches: ['notice_of_decision'] },
        { key: 'canadian_id_proof', label: 'Any Canadian ID Proof', doc_type: 'canadian_id_proof', matches: ['canadian_id_proof', 'driving_license_or_bcid', 'driving_license', 'bcid', 'provincial_id'] },
      ];
    default:
      // "Other" — no strict list; recruiter uses the "Additional Documents"
      // uploader to attach whatever the college needs.
      return [];
  }
}

// Conditional slot — only required when the selected preferred college flags it
const DECLARATION_SLOT = { key: 'student_declaration_form', label: 'Student Declaration Form', doc_type: 'student_declaration_form', matches: ['student_declaration_form'] };

// Intake dropdown options — first Monday of Oct/Jan/Apr/Jul, starting Oct 2026
// and going forward 3 years so recruiters can plan ahead.
const INTAKE_MONTH_LABELS = { 0: 'Jan', 3: 'Apr', 6: 'Jul', 9: 'Oct' };
function _firstMonday(year, monthIdx) {
  const d = new Date(year, monthIdx, 1);
  const offset = (1 - d.getDay() + 7) % 7; // Sunday=0, Monday=1
  return new Date(year, monthIdx, 1 + offset);
}
function buildIntakeOptions() {
  const opts = [];
  // Start at Oct 2026 → Jan 2027 → Apr 2027 → Jul 2027 → …
  let year = 2026;
  let monthIdx = 9; // October
  for (let i = 0; i < 16; i += 1) { // 16 quarters ≈ 4 years of options
    const monday = _firstMonday(year, monthIdx);
    const iso = monday.toISOString().slice(0, 10);
    const label = `${INTAKE_MONTH_LABELS[monthIdx]} ${year}`;
    const pretty = `${label} — first Monday, ${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    opts.push({ value: iso, label, pretty });
    // Advance 3 months
    monthIdx += 3;
    if (monthIdx > 11) { monthIdx -= 12; year += 1; }
  }
  return opts;
}
const INTAKE_OPTIONS = buildIntakeOptions();

export default function CreateStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const basePath = isAdmin ? '/admin' : '/recruiter';
  // Recruiters shouldn't see the raw filename of English-test proofs — admin
  // Accuplacer generators produce filenames like
  // "Exnum 76780543 Branching Profile Name 01. Reading, Writing, WritePlacer.pdf"
  // which leaks that the report was system-generated. Admins keep seeing the
  // real filename so they can audit uploads.
  const displayFileName = (f) => {
    if (!f) return '';
    const dt = (f.doc_type || '').toLowerCase();
    const isEnglishProof = dt === 'english_test_proof' || dt === 'english_test_certificate';
    if (!isAdmin && isEnglishProof) return 'English test proof uploaded';
    return f.original_filename || '';
  };
  const authConfig = {}; // Auth handled by axios interceptor
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('passport');
  const [showFillDialog, setShowFillDialog] = useState(false);
  const createdIdRef = useRef(id || null);
  const [createdId, setCreatedId] = useState(id || null);
  const [colleges, setColleges] = useState([]);
  const [recruitersForAdmin, setRecruitersForAdmin] = useState([]);
  // Recruiter's admin-approved sale price tiers — drives the Tuition Fee
  // dropdown at Step 3. Fetched once on mount for recruiters (admin creates
  // students on behalf of a specific recruiter so tiers are per-recruiter).
  const [myTiers, setMyTiers] = useState(null);        // null = loading, [] = none, [...] = tiers
  const [adminFee, setAdminFee] = useState(250);
  // Admin drag-and-drop auto-fill state
  const [extracting, setExtracting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // [{id, original_filename, doc_type, size, content_type}]
  const [aiFilledFields, setAiFilledFields] = useState({}); // {field: sourceFileName}
  const [dragOver, setDragOver] = useState(false);
  // Held only in local state; never sent to backend. Used to catch typos in the
  // student's email before the recruiter can submit.
  const [emailConfirm, setEmailConfirm] = useState('');
  // --- Autofill from ID & pre-submit ID-match gate ---
  const [autofillFileId, setAutofillFileId] = useState(null);       // currently extracting from this file
  const [autofillPreview, setAutofillPreview] = useState(null);     // { extracted: {...}, filename }
  const [showAutofillDialog, setShowAutofillDialog] = useState(false);
  const [idMatchFlags, setIdMatchFlags] = useState(null);           // { fields: [...], flagged_fields: [...] }
  const [idMatchOverrides, setIdMatchOverrides] = useState({});     // {name: 'reason', dob: 'reason', address: 'reason'}
  const [showIdMatchDialog, setShowIdMatchDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ID_SLOT_DOC_TYPES = ['canadian_passport', 'canadian_passport_or_citizenship', 'passport', 'home_country_passport', 'pr_card', 'driving_license', 'driving_license_or_bcid', 'bcid', 'provincial_id', 'photo_id', 'id_proof', 'canadian_id_proof', 'citizenship', 'citizenship_card', 'birth_certificate', 'canadian_birth_certificate'];
  useEffect(() => {
    if (id) setEmailConfirm(''); // Reset when editing an existing student
  }, [id]);
  const [form, setForm] = useState({
    full_name: '', first_name: '', middle_name: '', last_name: '',
    dob: '', gender: '', nationality: '', email: '', phone: '',
    marital_status: '',
    address: '', address_line1: '', address_line2: '', city: '', province: '',
    postal_code: '', passport_pr_status: '', academic_history: '', preferred_program: '',
    preferred_intake: '', intake_date: '', intake_label: '', notes: '', needs_exam_prep: false, exam_prep_types: [],
    preferred_college_id: '', preferred_college_name: '',
    preferred_program_id: '', preferred_program_name: '',
    preferred_branch: '',
    sale_price: '',
    is_serious: null,
    // Mature-student track drives a different doc list (see slotsForStatus).
    // Only shown when status is 'pr' or 'citizen'.
    is_mature_student: null,
    // Optional shortcut for mature students — if they have a 2-yr Canadian
    // diploma they can skip the English test ladder entirely.
    has_canadian_2yr_diploma: null,
    // PR conditional Q&A — tri-state (null = not asked / not applicable).
    english_test_proof: null,
    canadian_transcript_2yr: null,
    accuplacer_intent: null,
    status: 'draft',
    // Admin-only — which recruiter this profile is placed under
    recruiter_id: '',
  });

  useEffect(() => {
    axios.get(`${API}/recruiter/colleges`, authConfig).then(res => setColleges(res.data)).catch(() => {});
    if (isAdmin) {
      axios.get(`${API}/admin/recruiters?limit=500`, authConfig)
        .then(res => setRecruitersForAdmin(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    }
    // Fetch the current admin fee so we can display "sale + fee = total"
    axios.get(`${API}/admin-fee`).then(res => setAdminFee(Number(res.data?.amount || 250))).catch(() => {});
    // Recruiters need their tier list to pick a tuition fee. Admins don't —
    // they can type any sale price and pick which recruiter this profile
    // is placed under.
    if (!isAdmin) {
      axios.get(`${API}/recruiter/me/tiers`)
        .then(res => {
          setMyTiers(res.data?.tiers || []);
        })
        .catch(() => { setMyTiers([]); });
    }
  }, [isAdmin]);

  useEffect(() => {
    if (id) {
      createdIdRef.current = id;
      setCreatedId(id);
      axios.get(`${API}/students/${id}`, authConfig).then(res => {
        const data = res.data || {};
        // Backfill split name from full_name for older records that don't have
        // first_name / last_name populated yet.
        if (data.full_name && !data.first_name && !data.last_name) {
          const parts = String(data.full_name).trim().split(/\s+/);
          if (parts.length === 1) {
            data.first_name = parts[0];
          } else if (parts.length === 2) {
            [data.first_name, data.last_name] = parts;
          } else {
            data.first_name = parts[0];
            data.last_name = parts[parts.length - 1];
            data.middle_name = parts.slice(1, -1).join(' ');
          }
        }
        // Backfill Line-1 from single-string address so recruiter sees their data.
        if (data.address && !data.address_line1) {
          data.address_line1 = data.address;
        }
        setForm(prev => ({ ...prev, ...data }));
      }).catch(() => toast.error('Failed to load student'));
      axios.get(`${API}/files/student/${id}`, authConfig).then(res => setFiles(res.data)).catch(() => {});
    }
  }, [id]);

  // Whenever any part of the split name/address changes, keep the combined
  // ``full_name`` and ``address`` in sync so the rest of the app (exports,
  // Institution Portal, DocuSign, emails) keeps working unchanged.
  const composedFullName = [form.first_name, form.middle_name, form.last_name]
    .map(s => (s || '').trim()).filter(Boolean).join(' ');
  const composedAddress = [form.address_line1, form.address_line2, form.city, form.province, form.postal_code]
    .map(s => (s || '').trim()).filter(Boolean).join(', ');
  useEffect(() => {
    if (composedFullName && composedFullName !== form.full_name) {
      setForm(prev => ({ ...prev, full_name: composedFullName }));
    }
  }, [composedFullName, form.full_name]);
  useEffect(() => {
    if (composedAddress && composedAddress !== form.address) {
      setForm(prev => ({ ...prev, address: composedAddress }));
    }
  }, [composedAddress, form.address]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Admin drag-and-drop → GPT-4o Vision extraction → form auto-fill
  const handleExtractDocs = async (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (!arr.length) return;
    if (extracting) return;
    setExtracting(true);
    const toastId = toast.loading(`Reading ${arr.length} document${arr.length > 1 ? 's' : ''} with AI…`);
    try {
      const fd = new FormData();
      arr.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/admin/students/extract-profile`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { extracted = {}, pending_files = [], errors = [] } = res.data || {};

      // Merge non-empty extracted fields into the form; keep any manually-typed data.
      const filledMap = {};
      setForm(prev => {
        const next = { ...prev };
        const fieldSources = arr.length === 1 ? (arr[0].name || 'document') : `${arr.length} documents`;
        for (const k of ['full_name', 'dob', 'gender', 'nationality', 'email', 'phone', 'address', 'passport_pr_status', 'academic_history']) {
          const v = (extracted[k] || '').toString().trim();
          if (v && !(prev[k] || '').toString().trim()) {
            next[k] = v;
            filledMap[k] = fieldSources;
          }
        }
        return next;
      });
      setAiFilledFields(prev => ({ ...prev, ...filledMap }));
      setPendingFiles(prev => [...prev, ...pending_files]);

      const filledCount = Object.keys(filledMap).length;
      toast.dismiss(toastId);
      if (filledCount > 0) {
        toast.success(`Auto-filled ${filledCount} field${filledCount > 1 ? 's' : ''} from ${pending_files.length} document${pending_files.length > 1 ? 's' : ''}. Review and edit before saving.`);
      } else {
        toast.info(`Uploaded ${pending_files.length} document${pending_files.length > 1 ? 's' : ''}, but couldn\u2019t confidently extract any fields. Please fill the form manually.`);
      }
      if (errors.length) errors.forEach(e => toast.error(e));
    } catch (err) {
      toast.dismiss(toastId);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'AI extraction failed. Please fill the form manually.');
    } finally {
      setExtracting(false);
    }
  };

  const handleRemovePendingFile = (fileId) => {
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const onDropZoneClick = () => {
    document.getElementById('admin-extract-file-input')?.click();
  };

  const onDropZoneDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleExtractDocs(e.dataTransfer?.files);
  };

  const handleCollegeChange = (collegeId) => {
    const c = colleges.find(x => x.id === collegeId);
    // Compute available branches and auto-select if only one
    const branches = c && Array.isArray(c.locations) && c.locations.length > 0
      ? c.locations
      : (c?.location ? [c.location] : []);
    setForm(prev => ({
      ...prev,
      preferred_college_id: collegeId === 'none' ? '' : collegeId,
      preferred_college_name: c?.name || '',
      preferred_program_id: '',
      preferred_program_name: '',
      preferred_branch: branches.length === 1 ? branches[0] : '',
    }));
  };
  const handleProgramChange = (programId) => {
    const c = colleges.find(x => x.id === form.preferred_college_id);
    const p = c?.programs?.find(x => x.id === programId);
    setForm(prev => ({
      ...prev,
      preferred_program_id: programId === 'none' ? '' : programId,
      preferred_program_name: p?.name || '',
    }));
  };
  const selectedCollege = colleges.find(c => c.id === form.preferred_college_id);

  const toggleExamType = (type) => {
    setForm(prev => ({
      ...prev,
      exam_prep_types: prev.exam_prep_types.includes(type)
        ? prev.exam_prep_types.filter(t => t !== type)
        : [...prev.exam_prep_types, type],
    }));
  };

  const handleSave = async (status = 'draft', silent = false) => {
    if (isAdmin && !form.recruiter_id) {
      if (!silent) toast.error('Please select a Recruiter for this student before saving.');
      return null;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        status,
        sale_price: form.sale_price === '' || form.sale_price === null ? 0 : parseFloat(form.sale_price) || 0,
      };
      // Normalize Canadian postal code — insert missing space, force upper-case
      if (payload.postal_code) {
        const compact = String(payload.postal_code).replace(/\s+/g, '').toUpperCase();
        payload.postal_code = compact.length === 6 ? `${compact.slice(0, 3)} ${compact.slice(3)}` : compact;
      }
      if (isAdmin && !createdIdRef.current && pendingFiles.length) {
        payload.pending_file_ids = pendingFiles.map(f => f.id);
      }
      const effectiveId = createdIdRef.current;
      if (effectiveId) {
        await axios.put(`${API}/students/${effectiveId}`, payload, authConfig);
        if (!silent) toast.success(status === 'submitted' ? 'Application submitted!' : 'Student saved!');
        return effectiveId;
      } else {
        const res = await axios.post(`${API}/students`, payload, authConfig);
        createdIdRef.current = res.data.id;
        setCreatedId(res.data.id);
        // Clear pending list — files are now linked to the new student
        setPendingFiles([]);
        setAiFilledFields({});
        if (!silent) toast.success('Student created!');
        navigate(`${basePath}/student/${res.data.id}`, { replace: true });
        return res.data.id;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (!silent) toast.error(typeof detail === 'string' ? detail : 'Failed to save student');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // --- Autofill from ID (recruiter clicks "Autofill from this ID" on an uploaded ID doc) ---
  const handleAutofillFromId = async (fileRec) => {
    if (!fileRec?.id) return;
    setAutofillFileId(fileRec.id);
    const t = toast.loading('Reading the ID with AI…');
    try {
      const { data } = await axios.post(`${API}/students/extract-profile-from-file`, { file_id: fileRec.id }, authConfig);
      toast.dismiss(t);
      if (data.error) {
        toast.error(`Couldn't read the ID: ${data.error}`);
        setAutofillFileId(null);
        return;
      }
      setAutofillPreview({ extracted: data.extracted, filename: data.filename, doc_type: data.doc_type });
      setShowAutofillDialog(true);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || 'Could not read the ID');
    } finally {
      setAutofillFileId(null);
    }
  };

  // Apply the fields the recruiter ticked in the preview dialog.
  const applyAutofill = (fieldsToApply) => {
    const ex = autofillPreview?.extracted || {};
    setForm(prev => {
      const next = { ...prev };
      for (const k of fieldsToApply) {
        if (ex[k] !== undefined && ex[k] !== '') next[k] = ex[k];
      }
      // If we accepted first/last, recompose full_name.
      if (fieldsToApply.includes('first_name') || fieldsToApply.includes('last_name')) {
        next.full_name = [next.first_name, next.middle_name, next.last_name].filter(Boolean).join(' ').trim();
      }
      return next;
    });
    setShowAutofillDialog(false);
    setAutofillPreview(null);
    toast.success(`Autofilled ${fieldsToApply.length} field(s) from the ID. Please double-check before submitting.`);
  };

  const handleSubmit = async () => {
    // Validate all required fields
    const required = [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'dob', label: 'Date of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'nationality', label: 'Nationality' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'address_line1', label: 'Street Address' },
      { key: 'city', label: 'City' },
      { key: 'province', label: 'Province' },
      { key: 'postal_code', label: 'Postal Code' },
      { key: 'passport_pr_status', label: 'Status in Canada' },
      { key: 'preferred_college_id', label: 'Preferred College' },
      { key: 'preferred_program_id', label: 'Preferred Program' },
      { key: 'intake_date', label: 'Intake' },
    ];
    const missing = required.filter(r => !form[r.key] || String(form[r.key]).trim() === '').map(r => r.label);
    // Tuition Fee is required and must be > 0.
    const tuition = form.sale_price === '' || form.sale_price === null ? 0 : parseFloat(form.sale_price) || 0;
    if (!tuition || tuition <= 0) missing.push('Tuition Fee');
    // Branch is required only if the selected college has more than one branch
    const branches = selectedCollege && Array.isArray(selectedCollege.locations) && selectedCollege.locations.length > 0
      ? selectedCollege.locations
      : (selectedCollege?.location ? [selectedCollege.location] : []);
    if (branches.length > 1 && !form.preferred_branch) {
      missing.push('Campus / Branch');
    }
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(', ')}`);
      return;
    }
    // Confirm-email guard — only enforced when NOT editing an existing student
    // (edit mode never asks for the confirm field).
    if (!id) {
      const a = (form.email || '').trim().toLowerCase();
      const b = (emailConfirm || '').trim().toLowerCase();
      if (!b || a !== b) {
        toast.error("The two email fields don't match. Please re-type the email to confirm.");
        return;
      }
    }
    // Validate Canadian postal-code format after stripping any spacing/dashes.
    // Canada Post spec: 6 chars, "LDL DLD" pattern.
    // First letter (FSA): A B C E G H J K L M N P R S T V X Y  (no D F I O Q U W Z).
    // Middle & last letters (LDU): everything above PLUS W and Z (only D F I O Q U forbidden).
    const postalCompact = String(form.postal_code || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const postalRe = /^[ABCEGHJ-NPR-TVXY]\d[ABCEGHJ-NPR-TV-Z]\d[ABCEGHJ-NPR-TV-Z]\d$/;
    if (!postalRe.test(postalCompact)) {
      toast.error('Please enter a valid Canadian postal code (e.g. V5V 3E3)');
      return;
    }
    const effectiveId = createdIdRef.current;
    if (!effectiveId) {
      toast.error('Please upload all mandatory documents first (we\'ll save automatically).');
      return;
    }
    const missingDocs = getMissingSlots();
    if (missingDocs.length > 0) {
      toast.error(`Missing mandatory documents: ${missingDocs.map(m => m.label).join(', ')}`);
      return;
    }
    // Save silently first — the /submit endpoint drives the final success
    // or error toast so recruiters never see a misleading "submitted!"
    // right before a "missing document" red banner.
    await handleSave('submitted', true);
    // --- ID-match preflight: run before Submit so the recruiter can fix
    // typos BEFORE the backend hard-blocks them.
    try {
      const { data: pre } = await axios.post(`${API}/students/${effectiveId}/preflight-id-match`, {
        full_name: form.full_name,
        first_name: form.first_name,
        middle_name: form.middle_name,
        last_name: form.last_name,
        dob: form.dob,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        province: form.province,
        postal_code: form.postal_code,
      }, authConfig);
      if (pre.status === 'ok' && (pre.flagged_fields || []).length > 0) {
        setIdMatchFlags(pre);
        setIdMatchOverrides({});
        setShowIdMatchDialog(true);
        return;   // Wait for recruiter to fix or override
      }
    } catch (err) {
      // Preflight is best-effort — if it fails, fall through to real submit
      // which will enforce the gate server-side anyway.
      // eslint-disable-next-line no-console
      console.warn('ID preflight check failed', err);
    }
    await finalSubmit(effectiveId, {});
  };

  const finalSubmit = async (effectiveId, overrides) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/students/${effectiveId}/submit`, { id_match_overrides: overrides || {} }, authConfig);
      toast.success('Application submitted successfully — redirecting to dashboard…');
      setShowIdMatchDialog(false);
      setIdMatchFlags(null);
      setTimeout(() => navigate(basePath, { replace: true }), 900);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Server-side gate also returns id_match_mismatch — surface it in the modal.
      if (err?.response?.status === 422 && detail && typeof detail === 'object' && detail.code === 'id_match_mismatch') {
        setIdMatchFlags({ fields: detail.fields || [], flagged_fields: detail.flagged_fields || [] });
        setShowIdMatchDialog(true);
        toast.error(detail.message || 'The info does not match the uploaded ID. Please correct or override.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Application submission failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e, forcedDocType = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Auto-save draft if student not yet created. Use ref to avoid race on rapid clicks.
    let studentId = createdIdRef.current;
    if (!studentId) {
      if (!form.full_name?.trim()) {
        toast.error('Please enter the student\'s full name before uploading documents.');
        e.target.value = '';
        return;
      }
      if (isAdmin && !form.recruiter_id) {
        toast.error('Please select a Recruiter for this student before uploading documents.');
        e.target.value = '';
        return;
      }
      setUploading(true);
      try {
        const payload = {
          ...form,
          status: 'draft',
          sale_price: form.sale_price === '' || form.sale_price === null ? 0 : parseFloat(form.sale_price) || 0,
        };
        const res = await axios.post(`${API}/students`, payload, authConfig);
        studentId = res.data.id;
        createdIdRef.current = studentId;
        setCreatedId(studentId);
        toast.success('Draft saved — continuing upload…');
        window.history.replaceState(null, '', `${basePath}/student/${studentId}`);
      } catch {
        toast.error('Could not save draft before upload');
        setUploading(false);
        e.target.value = '';
        return;
      }
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('student_id', studentId);
      formData.append('doc_type', forcedDocType || selectedDocType);
      const res = await axios.post(`${API}/files/upload`, formData, { ...authConfig, headers: { 'Content-Type': 'multipart/form-data' } });
      setFiles(prev => [...prev, res.data]);
      // Surface any "uncertain — please double-check" warning the AI verifier attached.
      const warn = res.data?.verification_warning;
      if (warn) {
        toast.warning(`Uploaded, but please double-check: ${warn.reason || 'AI could not confirm this doc type.'}`, { duration: 8000 });
      } else {
        toast.success('Document uploaded and verified ✓');
      }
      if (!id && studentId) {
        navigate(`${basePath}/student/${studentId}`, { replace: true });
      }
    } catch (err) {
      // Verifier hard-block returns a structured 422 detail — surface it clearly.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422 && detail && typeof detail === 'object' && detail.code === 'doc_type_mismatch') {
        toast.error(detail.message || 'Wrong document for this slot.', { duration: 10000 });
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Upload failed');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const isSlotFilled = (slot) => files.some(f => slot.matches.includes(f.doc_type));
  const getSlotFile = (slot) => files.find(f => slot.matches.includes(f.doc_type));
  // Slots switch based on the recruiter-picked "Status in Canada" AND
  // whether the student is a mature student.
  const mandatorySlots = slotsForStatus(form.passport_pr_status, form.is_mature_student === true);
  // Which flows show the mature-student question at all.
  const showMatureQuestion = ['pr', 'citizen'].includes(form.passport_pr_status);
  // Mature students with a 2-yr Canadian diploma bypass the English ladder.
  const matureBypass = form.is_mature_student === true && form.has_canadian_2yr_diploma === true;
  // Show the English/transcript/Accuplacer ladder for:
  //   - Any PR who isn't a mature student who bypassed via diploma
  //   - Any Citizen mature student without a diploma
  //   - Any PR mature student without a diploma
  const showEnglishLadder = (
    (form.passport_pr_status === 'pr' && form.is_mature_student !== true) ||
    (showMatureQuestion && form.is_mature_student === true && form.has_canadian_2yr_diploma === false)
  );
  // Hard-block: if the recruiter answered NO to English proof, NO to
  // Canadian transcript, and NO to Accuplacer intent — the student cannot
  // proceed. We surface a red panel and disable the Submit button.
  const isIneligible = (
    showEnglishLadder &&
    form.english_test_proof === false &&
    form.canadian_transcript_2yr === false &&
    form.accuplacer_intent === false
  );
  // Student Declaration is required when the selected college has it enabled
  // OR when the college name starts with "ITD" (covers the production duplicate
  // "ITD Canada" entries that haven't had the flag flipped yet). Admin can
  // override this OFF for the recruiter via /auth/me → declaration_form_enabled.
  // When admin is creating a student on behalf of a recruiter, honour that
  // recruiter's toggle instead of the admin's own auth/me payload.
  const isItdCollege = !!selectedCollege?.name && /\bitd\b/i.test(selectedCollege.name);
  const selectedAdminRecruiter = isAdmin ? recruitersForAdmin.find(r => r.id === form.recruiter_id) : null;
  const recruiterDeclarationEnabled = isAdmin
    ? (selectedAdminRecruiter ? selectedAdminRecruiter.declaration_form_enabled !== false : true)
    : (user?.declaration_form_enabled !== false);
  // SDF is required for every student EXCEPT when the recruiter has been
  // explicitly bypassed by admin (declaration_form_enabled === false on the
  // recruiter record). This is the "DECLARATION: OFF" pill you see in the
  // Admin → Recruiters table.
  const declarationRequired = recruiterDeclarationEnabled;
  // English Test Proof upload slot is required whenever the ladder is shown
  // AND the recruiter answered "yes" to having proof (regardless of PR /
  // Citizen or mature status).
  const needsEnglishTestProof = showEnglishLadder && form.english_test_proof === true;
  const getMissingSlots = () => {
    const base = mandatorySlots.filter(s => !isSlotFilled(s));
    if (declarationRequired && !isSlotFilled(DECLARATION_SLOT)) base.push(DECLARATION_SLOT);
    if (needsEnglishTestProof) {
      const proofSlot = { key: 'english_test_proof', label: 'English Test Proof', doc_type: 'english_test_proof', matches: ['english_test_proof', 'english_test_certificate'] };
      if (!isSlotFilled(proofSlot)) base.push(proofSlot);
    }
    // If the student answered "yes" to having a 2-yr Canadian transcript,
    // they must upload it — applies to every ladder path, not just PR.
    if (showEnglishLadder && form.english_test_proof === false && form.canadian_transcript_2yr === true) {
      const transcriptSlot = { key: 'canadian_transcript_2yr', label: 'Canadian 2-Year Transcript', doc_type: 'canadian_transcript_2yr', matches: ['canadian_transcript_2yr'] };
      if (!isSlotFilled(transcriptSlot)) base.push(transcriptSlot);
    }
    // Mature students with a Canadian diploma must upload it (they took the
    // shortcut and now owe us the proof).
    if (matureBypass) {
      const diplomaSlot = { key: 'canadian_2yr_diploma', label: '2-Year Canadian Diploma Proof', doc_type: 'canadian_2yr_diploma', matches: ['canadian_2yr_diploma', 'canadian_transcript_2yr'] };
      if (!isSlotFilled(diplomaSlot)) base.push(diplomaSlot);
    }
    return base;
  };

  const handleDownloadDeclarationTemplate = async () => {
    if (!form.preferred_college_id) { toast.error('Pick a college first'); return; }
    // Static fallback for any ITD branch — works even before admin uploads a template
    const triggerItdFallback = () => {
      const a = document.createElement('a');
      a.href = '/itd-student-declaration-form.pdf';
      a.download = 'ITD_Student_Declaration_Form.pdf';
      a.target = '_blank';
      document.body.appendChild(a); a.click(); a.remove();
    };
    try {
      const res = await axios.get(`${API}/colleges/${form.preferred_college_id}/declaration-template`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'Student_Declaration_Form.pdf'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      // Backend doesn't have a template for this college yet — fall back to the
      // bundled ITD blank form (covers North Vancouver + Vernon branches).
      if (isItdCollege) {
        triggerItdFallback();
        return;
      }
      toast.error(err?.response?.data?.detail || 'Template not available yet — ask admin to upload it.');
    }
  };

  const handleDeclarationGenerated = (fileDoc) => {
    // Replace any previous declaration in local state
    setFiles(prev => [...prev.filter(f => f.doc_type !== 'student_declaration_form'), fileDoc]);
    setShowFillDialog(false);
    toast.success('Declaration form filled and uploaded');
  };

  // ─── Progressive reveal gates ────────────────────────────────────────────
  // Editing an existing student → show every section immediately.
  // New draft → sections cascade in as the recruiter fills the previous one.
  const _emailMatches = !id || (emailConfirm.trim().toLowerCase() === (form.email || '').trim().toLowerCase());
  const personalDone = !!(form.first_name && form.last_name && form.dob && form.gender && form.nationality && form.email && form.phone && _emailMatches);
  const addressDone = !!(form.address_line1 && form.city && form.province && form.postal_code);
  const _branches = selectedCollege && Array.isArray(selectedCollege.locations) && selectedCollege.locations.length > 0
    ? selectedCollege.locations
    : (selectedCollege?.location ? [selectedCollege.location] : []);
  const academicDone = !!(form.preferred_college_id && form.preferred_program_id && form.intake_date && (_branches.length < 2 || form.preferred_branch));
  const tuitionDone = !!(parseFloat(form.sale_price) > 0);
  const statusDone = !!form.passport_pr_status;
  const showAddress = !!id || personalDone;
  const showAcademic = !!id || addressDone;
  const showTuition = !!id || academicDone;
  const showStatus = !!id || tuitionDone;
  const showDocuments = !!id || statusDone;
  const showSDF = (!!id || statusDone) && declarationRequired; // SDF is hidden entirely when the recruiter has DECLARATION: OFF
  const revealClass = 'animate-in fade-in-0 slide-in-from-bottom-3 duration-500';

  // Refs + auto-scroll: when a section transitions from hidden → visible on a
  // fresh draft, smoothly scroll to it so the recruiter doesn't miss the
  // newly-revealed card. Never fires on initial mount or in edit mode.
  const academicRef = useRef(null);
  const tuitionRef = useRef(null);
  const statusRef = useRef(null);
  const documentsRef = useRef(null);
  const sdfRef = useRef(null);
  const prevShownRef = useRef({ academic: false, tuition: false, status: false, documents: false, sdf: false });
  useEffect(() => {
    if (id) return; // edit mode — everything shows at once, don't scroll
    const flip = (key, next, ref) => {
      if (!prevShownRef.current[key] && next) {
        // Wait one frame so the DOM node is rendered before scrolling.
        requestAnimationFrame(() => {
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      prevShownRef.current[key] = next;
    };
    flip('academic', showAcademic, academicRef);
    flip('tuition', showTuition, tuitionRef);
    flip('status', showStatus, statusRef);
    flip('documents', showDocuments, documentsRef);
    flip('sdf', showSDF, sdfRef);
  }, [id, showAcademic, showTuition, showStatus, showDocuments, showSDF]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/40 via-white to-white relative" data-testid="create-student-page">
      {/* Decorative dot pattern — very subtle, adds depth without distracting */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.12) 1px, transparent 0)',
          backgroundSize: '24px 24px',
          maskImage: 'linear-gradient(to bottom, black, transparent 60%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black, transparent 60%)',
        }}
      />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero header */}
        <div className="relative overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-emerald-50 mb-8">
          <div className="absolute -top-16 -right-8 w-40 h-40 rounded-full bg-blue-100/40 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-8 w-40 h-40 rounded-full bg-emerald-100/40 blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-3 p-6 lg:p-7">
            <Button variant="ghost" onClick={() => navigate(basePath)} className="h-9 w-9 p-0 rounded-full bg-white/70 backdrop-blur border border-gray-200 hover:bg-white" data-testid="back-btn" title="Back">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-blue-700/80">{isAdmin ? 'Admin' : 'Recruiter'} Workspace</p>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight mt-0.5" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                {id ? 'Edit Student Profile' : 'Create Student Profile'}
              </h1>
              <p className="text-gray-600 text-sm mt-1 max-w-lg">
                {isAdmin
                  ? 'Create a student and place them under any recruiter\u2019s profile.'
                  : 'Fill each section — the form saves as a draft automatically, so it\u2019s okay to pause and come back.'}
              </p>
            </div>
          </div>
        </div>

        {/* Step Indicator — pill trail showing progress */}
        {!id && (
          <div className="mb-6" data-testid="step-indicator">
            <StepTrail
              steps={[
                { key: 'personal', label: 'Personal', done: personalDone && addressDone },
                { key: 'academic', label: 'Academic', done: academicDone, active: showAcademic && !academicDone },
                { key: 'tuition', label: 'Tuition', done: tuitionDone, active: showTuition && !tuitionDone },
                { key: 'status', label: 'Status', done: statusDone, active: showStatus && !statusDone },
                { key: 'documents', label: 'Documents', done: showDocuments && getMissingSlots().length === 0, active: showDocuments && getMissingSlots().length > 0 },
                { key: 'sdf', label: 'Declaration', done: false, active: showSDF },
              ]}
            />
          </div>
        )}

        <div className="space-y-6">
          {/* Institution Notes — messages from the college */}
          {id && Array.isArray(form.institution_comments) && form.institution_comments.length > 0 && (
            <div className="rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white shadow-sm overflow-hidden" data-testid="recruiter-institution-notes">
              <div className="px-5 py-3 border-b border-indigo-100 bg-white/70 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                  <ChatCircleDots size={16} weight="fill" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                    Notes from the college ({form.institution_comments.length})
                  </h2>
                  <p className="text-[11px] text-gray-500">Direct messages from the institution reviewing this student.</p>
                </div>
              </div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                {[...form.institution_comments].reverse().map(c => (
                  <div key={c.id} className="p-3 rounded-lg border border-indigo-200 bg-white" data-testid={`institution-note-${c.id}`}>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{c.comment}</p>
                    <p className="text-[10px] text-gray-500 mt-1.5">
                      {c.author_name || c.author_email} · {c.college_name ? `${c.college_name} · ` : ''}{c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAdmin && !id && (
            <div
              className={`bg-white border-2 border-dashed rounded-lg p-6 transition-all ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-blue-200'}`}
              data-testid="admin-ai-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDropZoneDrop}
              onClick={onDropZoneClick}
              role="button"
              tabIndex={0}
            >
              <input
                id="admin-extract-file-input"
                type="file"
                accept="image/*,.pdf"
                multiple
                className="hidden"
                onChange={(e) => { handleExtractDocs(e.target.files); e.target.value = ''; }}
                data-testid="admin-extract-file-input"
              />
              <div className="flex flex-col items-center gap-2 text-center pointer-events-none">
                <div className={`inline-flex items-center justify-center h-11 w-11 rounded-full ${extracting ? 'bg-blue-100' : 'bg-blue-50'}`}>
                  {extracting ? <CircleNotch size={20} className="text-blue-700 animate-spin" /> : <Sparkle size={20} className="text-blue-700" />}
                </div>
                <p className="text-base font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  {extracting ? 'Reading your documents with AI\u2026' : 'Drop passport, PR card & marksheet to auto-fill'}
                </p>
                <p className="text-xs text-gray-500 max-w-md">
                  {extracting
                    ? 'Extracting name, DOB, gender, nationality, address, and academic history from every page.'
                    : 'Multi-file drop supported (PDF, JPG, PNG). Files are attached to this student automatically on save.'}
                </p>
                {!extracting && (
                  <span className="text-[11px] text-blue-700 font-medium mt-1">Click to browse or drag files here</span>
                )}
              </div>
              {pendingFiles.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100" data-testid="admin-pending-files-list" onClick={(e) => e.stopPropagation()}>
                  <p className="text-[11px] uppercase tracking-widest text-gray-500 font-medium mb-2">Attached documents ({pendingFiles.length})</p>
                  <ul className="space-y-1.5">
                    {pendingFiles.map(pf => (
                      <li key={pf.id} className="flex items-center justify-between gap-2 text-sm bg-gray-50 border border-gray-200 rounded px-3 py-1.5" data-testid={`pending-file-${pf.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={14} className="text-gray-500 shrink-0" />
                          <span className="truncate text-gray-900">{pf.original_filename}</span>
                          <span className="text-[11px] uppercase tracking-wider text-blue-700 bg-blue-100 rounded px-1.5 py-0.5 shrink-0">
                            {DOC_TYPE_LABELS[pf.doc_type] || pf.doc_type}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemovePendingFile(pf.id)}
                          className="text-gray-400 hover:text-red-600 shrink-0"
                          data-testid={`pending-file-remove-${pf.id}`}
                          title="Remove"
                        >
                          <Trash size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {isAdmin && (
            <div className="bg-white border border-blue-200 rounded-lg p-6" data-testid="admin-recruiter-picker">
              <h2 className="text-lg font-semibold text-gray-900 mb-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>Placed Under Recruiter *</h2>
              <p className="text-xs text-gray-500 mb-3">This student will appear in the chosen recruiter&rsquo;s pipeline. Commission tiers, hide-commission and declaration-form settings from that recruiter apply.</p>
              <Select value={form.recruiter_id || 'none'} onValueChange={(v) => handleChange('recruiter_id', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="admin-recruiter-select"><SelectValue placeholder="Select recruiter" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">&mdash; Select recruiter &mdash;</SelectItem>
                  {recruitersForAdmin.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.full_name || r.email} {r.company_name ? `\u2014 ${r.company_name}` : ''} ({r.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.recruiter_id && !selectedAdminRecruiter && form.recruiter_name && (
                <p className="text-[11px] text-gray-600 mt-2" data-testid="admin-recruiter-current">
                  Currently: <span className="font-medium">{form.recruiter_name}</span> ({form.recruiter_email || '—'})
                </p>
              )}
              {selectedAdminRecruiter && (
                <p className="text-[11px] text-gray-500 mt-2" data-testid="admin-recruiter-hint">
                  Flat commission: {selectedAdminRecruiter.commission_per_student ? `$${Number(selectedAdminRecruiter.commission_per_student).toLocaleString()}` : '—'}
                  {selectedAdminRecruiter.hide_commission ? ' \u00b7 Commission hidden from recruiter' : ''}
                  {selectedAdminRecruiter.declaration_form_enabled === false ? ' \u00b7 Declaration form OFF' : ''}
                </p>
              )}
            </div>
          )}

          {/* Personal Info */}
          <div className="relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm p-6 pl-7" data-testid="personal-info-section">
            <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500" aria-hidden />
            <SectionHeader icon={UserCircle} num={1} title="Personal Information" subtitle="Who is the student? Basic details + Canadian address." tone="blue" />
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label>First Name *</Label>
                <Input value={form.first_name} onChange={e => handleChange('first_name', e.target.value)} data-testid="student-first-name-input" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Middle Name</Label>
                <Input value={form.middle_name} onChange={e => handleChange('middle_name', e.target.value)} placeholder="optional" data-testid="student-middle-name-input" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Last Name *</Label>
                <Input value={form.last_name} onChange={e => handleChange('last_name', e.target.value)} data-testid="student-last-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Date of Birth *</Label>
                <Input type="date" value={form.dob} onChange={e => handleChange('dob', e.target.value)} data-testid="student-dob-input" />
              </div>
              <div className="space-y-2">
                <Label>Gender *</Label>
                <Select value={form.gender} onValueChange={v => handleChange('gender', v)}>
                  <SelectTrigger data-testid="student-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Marital Status</Label>
                <Select value={form.marital_status || 'none'} onValueChange={v => handleChange('marital_status', v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="student-marital-status-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not specified —</SelectItem>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married</SelectItem>
                    <SelectItem value="divorced">Divorced</SelectItem>
                    <SelectItem value="widowed">Widowed</SelectItem>
                    <SelectItem value="common_law">Common-law</SelectItem>
                    <SelectItem value="separated">Separated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nationality *</Label>
                <Select value={form.nationality || 'none'} onValueChange={v => handleChange('nationality', v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="student-nationality-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— Not selected —</SelectItem>
                    {COUNTRIES.map(c => (
                      <SelectItem key={c} value={c} data-testid={`nationality-option-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} data-testid="student-email-input" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Confirm Email *</Label>
                <Input
                  type="email"
                  value={emailConfirm}
                  onChange={e => setEmailConfirm(e.target.value)}
                  onPaste={e => e.preventDefault()}
                  placeholder="Re-type the email to confirm"
                  data-testid="student-email-confirm-input"
                  className={emailConfirm && emailConfirm.trim().toLowerCase() !== (form.email || '').trim().toLowerCase() ? 'border-red-400 focus-visible:ring-red-200' : ''}
                />
                {emailConfirm && emailConfirm.trim().toLowerCase() !== (form.email || '').trim().toLowerCase() && (
                  <p className="text-[11px] text-red-600" data-testid="student-email-mismatch">Emails don&rsquo;t match yet.</p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Phone *</Label>
                <PhoneInput
                  value={form.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  countryCode={form.phone_code || '+1'}
                  onCountryCodeChange={v => handleChange('phone_code', v)}
                  data-testid="student-phone-input"
                />
              </div>
            </div>

            {/* Canadian-standard address block */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-3">Address (Canadian format)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Street Address *</Label>
                  <Input value={form.address_line1} onChange={e => handleChange('address_line1', e.target.value)} placeholder="e.g. 123 Main Street" data-testid="student-address-line1-input" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Apt / Unit / Suite</Label>
                  <Input value={form.address_line2} onChange={e => handleChange('address_line2', e.target.value)} placeholder="optional — e.g. Apt 5B" data-testid="student-address-line2-input" />
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input value={form.city} onChange={e => handleChange('city', e.target.value)} placeholder="e.g. Vancouver" data-testid="student-city-input" />
                </div>
                <div className="space-y-2">
                  <Label>Province *</Label>
                  <Select value={form.province} onValueChange={v => handleChange('province', v)}>
                    <SelectTrigger data-testid="student-province-select"><SelectValue placeholder="Select province / territory" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AB">Alberta</SelectItem>
                      <SelectItem value="BC">British Columbia</SelectItem>
                      <SelectItem value="MB">Manitoba</SelectItem>
                      <SelectItem value="NB">New Brunswick</SelectItem>
                      <SelectItem value="NL">Newfoundland and Labrador</SelectItem>
                      <SelectItem value="NS">Nova Scotia</SelectItem>
                      <SelectItem value="ON">Ontario</SelectItem>
                      <SelectItem value="PE">Prince Edward Island</SelectItem>
                      <SelectItem value="QC">Quebec</SelectItem>
                      <SelectItem value="SK">Saskatchewan</SelectItem>
                      <SelectItem value="NT">Northwest Territories</SelectItem>
                      <SelectItem value="NU">Nunavut</SelectItem>
                      <SelectItem value="YT">Yukon</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Postal Code *</Label>
                  <Input
                    value={form.postal_code}
                    onChange={e => {
                      const compact = String(e.target.value)
                        .replace(/[^A-Za-z0-9]/g, '')
                        .toUpperCase()
                        .slice(0, 6);
                      const formatted = compact.length > 3
                        ? `${compact.slice(0, 3)} ${compact.slice(3)}`
                        : compact;
                      handleChange('postal_code', formatted);
                    }}
                    placeholder="A1A 1A1"
                    maxLength={7}
                    data-testid="student-postal-code-input"
                  />
                </div>
              </div>
            </div>

          </div>

          {/* Academic */}
          {showAcademic && (
          <div ref={academicRef} className={`relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm p-6 pl-7 ${revealClass}`} data-testid="academic-section">
            <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-500" aria-hidden />
            <SectionHeader icon={GraduationCap} num={2} title="Academic & Program" subtitle="Pick the destination college, program, and intake month." tone="indigo" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preferred College *</Label>
                <Select value={form.preferred_college_id || 'none'} onValueChange={handleCollegeChange}>
                  <SelectTrigger data-testid="student-preferred-college-select"><SelectValue placeholder="Select college" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not selected —</SelectItem>
                    {colleges.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(() => {
                const branches = selectedCollege && Array.isArray(selectedCollege.locations) && selectedCollege.locations.length > 0
                  ? selectedCollege.locations
                  : (selectedCollege?.location ? [selectedCollege.location] : []);
                if (!selectedCollege || branches.length < 2) return null;
                return (
                  <div className="space-y-2">
                    <Label>Campus / Branch *</Label>
                    <Select
                      value={form.preferred_branch || 'none'}
                      onValueChange={(v) => setForm(prev => ({ ...prev, preferred_branch: v === 'none' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="student-preferred-branch-select"><SelectValue placeholder="Pick a campus" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Not selected —</SelectItem>
                        {branches.map((loc, i) => (
                          <SelectItem key={i} value={loc}>{loc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
              <div className="space-y-2">
                <Label>Preferred Program *</Label>
                <Select
                  value={form.preferred_program_id || 'none'}
                  onValueChange={handleProgramChange}
                  disabled={!form.preferred_college_id || !(selectedCollege?.programs?.length)}
                >
                  <SelectTrigger data-testid="student-preferred-program-select"><SelectValue placeholder={form.preferred_college_id ? (selectedCollege?.programs?.length ? 'Select program' : 'No programs listed for this college') : 'Pick a college first'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not selected —</SelectItem>
                    {selectedCollege?.programs?.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Intake *</Label>
                <Select
                  value={form.intake_date || 'none'}
                  onValueChange={(val) => {
                    if (val === 'none') {
                      handleChange('intake_date', '');
                      handleChange('intake_label', '');
                    } else {
                      const opt = INTAKE_OPTIONS.find(o => o.value === val);
                      handleChange('intake_date', val);
                      handleChange('intake_label', opt?.label || '');
                    }
                  }}
                >
                  <SelectTrigger data-testid="student-intake-select">
                    <SelectValue placeholder="Select intake" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— Not selected —</SelectItem>
                    {INTAKE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} data-testid={`intake-option-${opt.value}`}>
                        {opt.pretty}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-500">Program start date — first Monday of the intake month.</p>
              </div>
            </div>
          </div>
          )}

          {/* Tuition Fee — recruiter picks from admin-approved sale price tiers. */}
          {showTuition && (
          <div ref={tuitionRef} className={`relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm p-6 pl-7 ${revealClass}`} data-testid="pricing-section">
            <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-500" aria-hidden />
            <SectionHeader icon={CurrencyDollar} num={3} title="Tuition Fee" subtitle="Pick a tuition fee from the sale prices your admin has approved for you." tone="emerald" />
            {isAdmin ? (
              // Admin flow — admins keep the free-text input so they can override any recruiter's price
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tuition Fee (CAD) *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={form.sale_price}
                      onChange={e => handleChange('sale_price', e.target.value)}
                      placeholder="e.g. 17500"
                      className="pl-7"
                      data-testid="student-sale-price-input"
                    />
                  </div>
                </div>
              </div>
            ) : myTiers === null ? (
              <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="tuition-loading">
                <CircleNotch size={14} className="animate-spin" /> Loading your approved sale prices…
              </div>
            ) : myTiers.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="tuition-no-tiers">
                <p className="font-semibold">No sale prices approved yet</p>
                <p className="mt-1 text-[13px] text-amber-800">
                  Please ask your admin to set your Commission Tiers before creating a student profile. Once tiers are configured, they will appear here for you to choose from.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tuition-tier-picker">
                <div className="space-y-2">
                  <Label>Tuition Fee (CAD) *</Label>
                  <Select
                    value={form.sale_price ? String(form.sale_price) : ''}
                    onValueChange={(v) => handleChange('sale_price', v)}
                  >
                    <SelectTrigger data-testid="student-sale-price-select" className="h-11">
                      <SelectValue placeholder="Pick an approved sale price…" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...myTiers]
                        .sort((a, b) => Number(a.sale_price) - Number(b.sale_price))
                        .map(t => {
                          const sp = Number(t.sale_price || 0);
                          return (
                            <SelectItem key={sp} value={String(sp)} data-testid={`sale-price-option-${sp}`}>
                              ${sp.toLocaleString()}
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-gray-500">Only sale prices your admin has approved appear here.</p>
                </div>
                {parseFloat(form.sale_price) > 0 && (
                  <div className="rounded-lg border border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-white p-4" data-testid="tuition-total-breakdown">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700/80">Total tuition shown to student</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                      ${(parseFloat(form.sale_price) + Number(adminFee || 0)).toLocaleString()}
                    </p>
                    <div className="mt-2 text-[12px] text-gray-600 space-y-0.5 tabular-nums">
                      <div className="flex justify-between"><span>Sale price</span><span>${parseFloat(form.sale_price).toLocaleString()}</span></div>
                      <div className="flex justify-between text-gray-500"><span>+ Admin fee</span><span>${Number(adminFee || 0).toLocaleString()}</span></div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Status in Canada — its own card, gates Documents */}
          {showStatus && (
          <div ref={statusRef} className={`relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm p-6 pl-7 ${revealClass}`} data-testid="status-section">
            <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-violet-500" aria-hidden />
            <SectionHeader icon={MapPin} num={4} title="Status in Canada" subtitle="Pick the student's current status — this decides the documents we'll ask for next." tone="violet" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="status-in-canada-section">
              {STATUS_OPTIONS.map(opt => {
                const active = form.passport_pr_status === opt.value;
                const toneMap = {
                  emerald: active ? 'border-emerald-400 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-200' : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/40',
                  blue:    active ? 'border-blue-400 bg-blue-50 text-blue-900 ring-2 ring-blue-200'         : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40',
                  violet:  active ? 'border-violet-400 bg-violet-50 text-violet-900 ring-2 ring-violet-200' : 'border-gray-200 hover:border-violet-300 hover:bg-violet-50/40',
                  amber:   active ? 'border-amber-400 bg-amber-50 text-amber-900 ring-2 ring-amber-200'     : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/40',
                };
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        passport_pr_status: opt.value,
                        is_mature_student: null,
                        has_canadian_2yr_diploma: null,
                        english_test_proof: null,
                        canadian_transcript_2yr: null,
                        accuplacer_intent: null,
                      }));
                    }}
                    className={`text-left px-3 py-2.5 rounded-lg border transition-all ${toneMap[opt.tone]}`}
                    data-testid={`status-option-${opt.value}`}
                  >
                    <p className="text-sm font-semibold">{opt.label}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{opt.hint}</p>
                  </button>
                );
              })}
            </div>
            {/* Refugee onboarding checklist — surfaces the 3-doc requirement
                the moment "Refugee" is picked so recruiters can collect them
                upfront, before ITD Admissions reviews the file. */}
            {form.passport_pr_status === 'refugee' && (() => {
              const programEnd = computeProgramEndDate(form.intake_date);
              const friendlyEnd = formatFriendlyDate(programEnd);
              return (
              <div className="mt-5 p-4 rounded-lg border border-amber-300 bg-amber-50" data-testid="refugee-checklist-panel">
                <div className="flex items-start gap-2.5">
                  <ShieldCheck size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-amber-900">Refugee / Protected Person — 3 documents required</p>
                    <p className="text-xs text-amber-800 mt-1">
                      The <strong>ITD admissions team</strong> needs the 3 documents below to review this student. Collect them once, upfront — we&rsquo;ll re-use them at the Enrollment Agreement stage.
                    </p>
                    <ul className="mt-3 space-y-2 text-xs text-amber-900">
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center text-[10px] font-bold">1</span>
                        <span><strong>Government-issued Photo ID (Canadian)</strong> — Provincial ID card, Driving Licence, or Refugee Travel Document.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center text-[10px] font-bold">2</span>
                        <span>
                          <strong>SIN letter</strong> — the SIN number must be valid <em>past the program end date</em>
                          {friendlyEnd ? <> — for this student that means the SIN must not expire before <strong>{friendlyEnd}</strong> (program ends the Friday before the same intake month next year).</> : ' — pick an intake below and we\u2019ll show the exact date to check.'}
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center text-[10px] font-bold">3</span>
                        <span><strong>IRB Notice of Decision</strong> — the IRCC / IRB letter confirming Refugee or Protected Person status.</span>
                      </li>
                    </ul>
                    <p className="text-[11px] text-amber-700 mt-3 italic">All 3 uploads are enforced in the <strong>Documents</strong> section below — the profile cannot be submitted until they&rsquo;re on file.</p>
                  </div>
                </div>
              </div>
            );})()}

            {/* Mature-student — only for PR + Citizen. Drives a different
                mandatory doc list. */}
            {showMatureQuestion && (
              <div className="mt-5 pt-5 border-t border-gray-100" data-testid="mature-student-section">
                <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Is this student classified as a Mature Student?</Label>
                <p className="text-[12px] text-gray-500 mt-1">Colleges use this to decide whether Class 12 is required — mature students bypass the traditional academic gate.</p>
                <div className="flex items-center gap-2 mt-3 max-w-sm">
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, is_mature_student: true, has_canadian_2yr_diploma: prev.has_canadian_2yr_diploma }))}
                    className={`flex-1 h-10 rounded-md border text-sm font-medium transition-colors ${form.is_mature_student === true ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid="mature-yes-btn"
                  >Yes</button>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, is_mature_student: false, has_canadian_2yr_diploma: null, english_test_proof: null, canadian_transcript_2yr: null, accuplacer_intent: null }))}
                    className={`flex-1 h-10 rounded-md border text-sm font-medium transition-colors ${form.is_mature_student === false ? 'bg-amber-50 border-amber-400 text-amber-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid="mature-no-btn"
                  >No</button>
                </div>
                {form.is_mature_student === true && (
                  <div className="mt-4 p-3 rounded-lg border border-emerald-100 bg-emerald-50/40" data-testid="mature-diploma-section">
                    <p className="text-sm font-semibold text-gray-800">Does the student have a 2-year Canadian diploma?</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">If yes — no need to answer the English test questions. If no — we&rsquo;ll ask about English test proof next.</p>
                    <div className="flex items-center gap-2 mt-3 max-w-sm">
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, has_canadian_2yr_diploma: true, english_test_proof: null, canadian_transcript_2yr: null, accuplacer_intent: null }))}
                        className={`flex-1 h-10 rounded-md border text-sm font-medium transition-colors ${form.has_canadian_2yr_diploma === true ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        data-testid="mature-diploma-yes-btn"
                      >Yes</button>
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, has_canadian_2yr_diploma: false }))}
                        className={`flex-1 h-10 rounded-md border text-sm font-medium transition-colors ${form.has_canadian_2yr_diploma === false ? 'bg-amber-50 border-amber-400 text-amber-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        data-testid="mature-diploma-no-btn"
                      >No</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Documents */}
          {showDocuments && (
          <div ref={documentsRef} className={`relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm p-6 pl-7 ${revealClass}`} data-testid="documents-section">
            <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-amber-500" aria-hidden />
            <SectionHeader
              icon={Files}
              num={5}
              title="Documents"
              subtitle="Required documents change based on the status you picked above."
              tone="amber"
              badge={form.passport_pr_status
                ? `${mandatorySlots.filter(s => isSlotFilled(s)).length} / ${mandatorySlots.length} uploaded`
                : 'Pick status first'}
            />

            {!id && form.passport_pr_status && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg" data-testid="docs-prehint">
                <p className="text-xs text-blue-800">
                  <strong>Tip:</strong> Fill in the student&apos;s <strong>Full Name</strong> above, then click any <em>Upload</em> button below — we&apos;ll save the draft automatically.
                </p>
              </div>
            )}

            {!form.passport_pr_status && (
              <div className="p-6 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-center" data-testid="pick-status-first">
                <FileText size={22} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 font-medium">Pick the student&rsquo;s <strong>Status in Canada</strong> first</p>
                <p className="text-xs text-gray-500 mt-1">The required documents change based on whether they&rsquo;re a PR, Citizen, Refugee, or Other.</p>
              </div>
            )}

            {form.passport_pr_status && (
              <div className="space-y-3 mb-6" data-testid="mandatory-docs-section">
                {mandatorySlots.length > 0 && (
                  <p className="text-sm font-medium text-gray-700">Required Documents <span className="text-red-600">*</span></p>
                )}
                {mandatorySlots.map(slot => {
                const filled = isSlotFilled(slot);
                const f = getSlotFile(slot);
                const isIdSlot = filled && f && ID_SLOT_DOC_TYPES.includes(f.doc_type);
                return (
                  <div key={slot.key} className={`flex items-center gap-3 p-3 rounded-lg border ${filled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`mandatory-slot-${slot.key}`}>
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${filled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      <FileText size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{slot.label} {!filled && <span className="text-red-600">*</span>}</p>
                      {filled ? (
                        <p className="text-xs text-gray-600 truncate">{displayFileName(f)}</p>
                      ) : (
                        <p className="text-xs text-amber-700">Required — not uploaded yet</p>
                      )}
                    </div>
                    {isIdSlot && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleAutofillFromId(f)}
                        disabled={autofillFileId === f.id}
                        className="shrink-0 h-8 px-2.5 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        data-testid={`autofill-from-id-${slot.key}`}
                        title="Read the ID and pre-fill name, DOB and address into the form"
                      >
                        {autofillFileId === f.id
                          ? <><CircleNotch size={12} className="mr-1 animate-spin" /> Reading…</>
                          : <><Sparkle size={12} className="mr-1" /> Autofill from ID</>}
                      </Button>
                    )}
                    <Label htmlFor={`upload-${slot.key}`} className="cursor-pointer shrink-0">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filled ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-blue-700 text-white hover:bg-blue-800'}`}>
                        <UploadSimple size={14} />
                        {filled ? 'Replace' : 'Upload'}
                      </div>
                    </Label>
                    <input
                      id={`upload-${slot.key}`}
                      type="file"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, slot.doc_type)}
                      disabled={uploading}
                      data-testid={`mandatory-upload-input-${slot.key}`}
                    />
                  </div>
                );
              })}

              {/* Conditional Q&A ladder: english test → transcript → accuplacer.
                  Now driven by `showEnglishLadder` so it covers PR, Citizen
                  mature, and PR mature students. */}
              {showEnglishLadder && <PrConditionalLadder
                form={form}
                setForm={setForm}
                handleFileUpload={handleFileUpload}
                uploading={uploading}
                isSlotFilled={isSlotFilled}
                getSlotFile={getSlotFile}
                displayFileName={displayFileName}
              />}

              {/* Mature-student diploma upload — required when the mature
                  student took the "yes I have a 2-yr Canadian diploma"
                  shortcut. */}
              {matureBypass && (() => {
                const diplomaSlot = { key: 'canadian_2yr_diploma', label: '2-Year Canadian Diploma Proof', doc_type: 'canadian_2yr_diploma', matches: ['canadian_2yr_diploma', 'canadian_transcript_2yr'] };
                const filled = isSlotFilled(diplomaSlot);
                const file = getSlotFile(diplomaSlot);
                return (
                  <div className={`mt-4 flex items-center gap-3 p-3 rounded-lg border ${filled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="mature-diploma-upload">
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${filled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      <FileText size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">2-Year Canadian Diploma Proof <span className="text-red-600">*</span></p>
                      {filled
                        ? <p className="text-xs text-gray-600 truncate">{file?.original_filename}</p>
                        : <p className="text-xs text-amber-700">Upload the diploma / graduation certificate</p>}
                    </div>
                    <Label htmlFor="upload-mature-diploma" className="cursor-pointer shrink-0">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filled ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-blue-700 text-white hover:bg-blue-800'}`}>
                        <UploadSimple size={14} /> {filled ? 'Replace' : 'Upload'}
                      </div>
                    </Label>
                    <input
                      id="upload-mature-diploma"
                      type="file"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'canadian_2yr_diploma')}
                      disabled={uploading}
                    />
                  </div>
                );
              })()}
              </div>
            )}

            {/* Additional docs */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Additional Documents <span className="text-xs text-gray-400 font-normal">(English test score, photo, etc.)</span></p>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div className="space-y-2 flex-1 min-w-[200px]">
                  <Label>Document Type</Label>
                  <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map(dt => (
                        <SelectItem key={dt} value={dt}>{DOC_TYPE_LABELS[dt]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="file-upload" className="cursor-pointer">
                    <div className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-800 transition-colors">
                      <UploadSimple size={16} />
                      {uploading ? 'Uploading...' : 'Upload File'}
                    </div>
                  </Label>
                  <input id="file-upload" type="file" className="hidden" onChange={(e) => handleFileUpload(e)} disabled={uploading} data-testid="file-upload-input" />
                </div>
              </div>
              {files.filter(f => !mandatorySlots.some(s => s.matches.includes(f.doc_type))).length > 0 ? (
                <div className="space-y-2">
                  {files.filter(f => !mandatorySlots.some(s => s.matches.includes(f.doc_type))).map(f => (
                    <div key={f.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`file-item-${f.id}`}>
                      <FileText size={18} className="text-gray-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{displayFileName(f)}</p>
                        <p className="text-xs text-gray-500">{DOC_TYPE_LABELS[f.doc_type] || f.doc_type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No additional documents uploaded</p>
              )}
            </div>
          </div>
          )}

          {/* Student Declaration Form — always visible right before submit once status is picked */}
          {showSDF && (() => {
            const filled = isSlotFilled(DECLARATION_SLOT);
            const f = getSlotFile(DECLARATION_SLOT);
            return (
              <div ref={sdfRef} className={`relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm p-6 pl-7 ${revealClass}`} data-testid="declaration-card">
                <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-rose-500" aria-hidden />
                <SectionHeader icon={ShieldCheck} num={6} title="Student Declaration Form" subtitle="Last step — the student signs off before we submit their application." tone="rose" badge={filled ? 'Completed' : 'Required'} />
                <div className={`rounded-lg border ${filled ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} p-3 space-y-2`} data-testid="declaration-slot">
                  <div className="flex items-center gap-3">
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${filled ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      <FileText size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">Student Declaration Form {!filled && <span className="text-red-600">*</span>}</p>
                      {filled ? (
                        <p className="text-xs text-gray-600 truncate">{f?.original_filename}</p>
                      ) : isItdCollege ? (
                        <p className="text-xs text-blue-700">Required by {selectedCollege?.name}. Click <strong>Fill online</strong> below — uploads aren&apos;t accepted for ITD campuses.</p>
                      ) : (
                        <p className="text-xs text-blue-700">Required by {selectedCollege?.name}. Download the blank PDF or fill it online.</p>
                      )}
                    </div>
                    {!isItdCollege && (
                      <>
                        <Label htmlFor="upload-declaration" className="cursor-pointer shrink-0">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filled ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-blue-700 text-white hover:bg-blue-800'}`}>
                            <UploadSimple size={14} /> {filled ? 'Replace' : 'Upload Signed PDF'}
                          </div>
                        </Label>
                        <input
                          id="upload-declaration"
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, 'student_declaration_form')}
                          disabled={uploading}
                          data-testid="declaration-upload-input"
                        />
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-3 pl-11 text-xs">
                    {!isItdCollege && (
                      <>
                        <button
                          type="button"
                          onClick={handleDownloadDeclarationTemplate}
                          className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline"
                          data-testid="download-declaration-template"
                        >
                          <DownloadSimple size={12} /> Download blank form
                        </button>
                        <span className="text-gray-300">·</span>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        if (createdIdRef.current) { setShowFillDialog(true); return; }
                        if (!form.full_name?.trim()) {
                          toast.error("Type the student's full name above first");
                          return;
                        }
                        const sid = await handleSave('draft', true);
                        if (sid) {
                          toast.success('Draft saved — opening declaration form');
                          setShowFillDialog(true);
                        }
                      }}
                      className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline"
                      data-testid="fill-declaration-online"
                    >
                      <PencilSimple size={12} /> Fill online
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Actions */}
          <div className="pb-8 space-y-4">
            {/* Serious Student — moved here so it's the last decision a
                recruiter makes before submitting. */}
            {statusDone && (
              <div className="relative overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm p-5 pl-6" data-testid="serious-lead-section">
                <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-400" aria-hidden />
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700/80">Final Question</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">Is this a serious student?</p>
                    <p className="text-[12px] text-gray-500 mt-0.5">Helps admin prioritise follow-ups.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleChange('is_serious', true)}
                      className={`h-10 px-6 rounded-md border text-sm font-medium transition-colors ${form.is_serious === true ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      data-testid="serious-yes-btn"
                    >Yes</button>
                    <button
                      type="button"
                      onClick={() => handleChange('is_serious', false)}
                      className={`h-10 px-6 rounded-md border text-sm font-medium transition-colors ${form.is_serious === false ? 'bg-amber-50 border-amber-400 text-amber-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      data-testid="serious-no-btn"
                    >No</button>
                  </div>
                </div>
              </div>
            )}

            {/* Ineligibility gate — hard-blocks submission when the student
                answered "no" to English proof, transcript, and Accuplacer. */}
            {isIneligible && (
              <div className="relative overflow-hidden rounded-xl border border-red-200 bg-red-50 p-5 pl-6" data-testid="ineligibility-panel">
                <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500" aria-hidden />
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 text-red-700 border border-red-200 flex items-center justify-center">
                    <XCircle size={20} weight="fill" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-red-900">Student is not eligible</p>
                    <p className="text-[13px] text-red-800/90 mt-1">
                      Without English test proof, a 2-year Canadian transcript, or a willingness to take the Accuplacer test, colleges will not admit this student. Please revisit those questions above or discuss alternatives with the student before continuing.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-end">
              <Button variant="outline" onClick={() => handleSave('draft')} disabled={loading} data-testid="save-draft-btn">
                <FloppyDisk size={16} className="mr-2" />
                {loading ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button onClick={handleSubmit} disabled={loading || isIneligible} className="bg-blue-700 hover:bg-blue-800 text-white disabled:bg-gray-300 disabled:text-gray-500" data-testid="submit-student-btn" title={isIneligible ? 'Student is not eligible — see the panel above' : ''}>
                <PaperPlaneTilt size={16} className="mr-2" />
                {loading ? 'Submitting...' : (isIneligible ? 'Not eligible — cannot submit' : 'Submit Application')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Fill Declaration Online dialog */}
      {showFillDialog && (
        <FillDeclarationDialog
          open={showFillDialog}
          onClose={() => setShowFillDialog(false)}
          student={{ ...form, id: createdId }}
          collegeName={selectedCollege?.name}
          onGenerated={handleDeclarationGenerated}
        />
      )}

      {/* Autofill preview dialog — shows what the AI read, lets recruiter tick which fields to apply */}
      <Dialog open={showAutofillDialog} onOpenChange={setShowAutofillDialog}>
        <DialogContent className="max-w-xl" data-testid="autofill-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} className="text-indigo-600" /> Autofill from ID
            </DialogTitle>
            <DialogDescription>
              We read <span className="font-medium">{autofillPreview?.filename}</span> ({autofillPreview?.doc_type?.replace(/_/g, ' ')}).
              Review the fields below — only ticked rows will be copied into the form. Already-typed values will be overwritten.
            </DialogDescription>
          </DialogHeader>
          {autofillPreview && (() => {
            const ex = autofillPreview.extracted || {};
            const rows = [
              { key: 'first_name', label: 'First Name', val: ex.first_name },
              { key: 'middle_name', label: 'Middle Name', val: ex.middle_name },
              { key: 'last_name', label: 'Last Name', val: ex.last_name },
              { key: 'dob', label: 'Date of Birth', val: ex.dob },
              { key: 'address_line1', label: 'Street Address', val: ex.address_line1 },
              { key: 'city', label: 'City', val: ex.city },
              { key: 'province', label: 'Province', val: ex.province },
              { key: 'postal_code', label: 'Postal Code', val: ex.postal_code },
            ].filter(r => r.val && String(r.val).trim());
            if (rows.length === 0) {
              return <p className="text-sm text-gray-500 py-4">Nothing readable was extracted from this document. Try uploading a clearer scan.</p>;
            }
            return (
              <AutofillRows
                rows={rows}
                currentForm={form}
                onApply={applyAutofill}
                onCancel={() => setShowAutofillDialog(false)}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ID-match pre-submit gate — list each flagged field with Fix / Override */}
      <Dialog open={showIdMatchDialog} onOpenChange={(v) => { if (!v) { setShowIdMatchDialog(false); } }}>
        <DialogContent className="max-w-2xl" data-testid="id-match-gate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-amber-600" /> Please double-check before submitting
            </DialogTitle>
            <DialogDescription>
              The info you typed doesn't match the ID document you uploaded. Fix the typed value, or override with a short reason if the ID is outdated / legal name has changed.
            </DialogDescription>
          </DialogHeader>
          {idMatchFlags && (
            <div className="space-y-4">
              {(idMatchFlags.fields || []).map((f, i) => (
                <div key={f.field || i} className="border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid={`id-match-flag-${f.field}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle size={14} weight="fill" className="text-red-600" />
                    <span className="text-sm font-semibold capitalize">
                      {f.field === 'dob' ? 'Date of Birth' : f.field}
                    </span>
                    {f.doc_type && <span className="text-[10px] text-gray-500 uppercase tracking-wider">(ID: {f.doc_type.replace(/_/g, ' ')})</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">You typed</div>
                      <div className="text-gray-900 break-words">{f.profile_value || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">ID shows</div>
                      <div className="text-gray-900 break-words">{f.id_value || '—'}</div>
                    </div>
                  </div>
                  {f.reason && <div className="text-[11px] text-amber-800 mt-2">{f.reason}</div>}
                  <div className="mt-3">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-600 mb-1 block">
                      Override reason (leave blank to go fix the typed value instead)
                    </Label>
                    <Input
                      value={idMatchOverrides[f.field] || ''}
                      onChange={(e) => setIdMatchOverrides(prev => ({ ...prev, [f.field]: e.target.value }))}
                      placeholder="e.g., Legal name change, address updated after ID was issued…"
                      className="h-9 text-sm"
                      data-testid={`id-match-override-${f.field}`}
                    />
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowIdMatchDialog(false)}
                  data-testid="id-match-fix-btn"
                >
                  I'll fix the typed values
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    const needed = (idMatchFlags.flagged_fields || []);
                    const missing = needed.filter(k => !String(idMatchOverrides[k] || '').trim());
                    if (missing.length > 0) {
                      toast.error(`Please add an override reason for: ${missing.join(', ')} — or close this and fix the typed values.`);
                      return;
                    }
                    finalSubmit(createdIdRef.current, idMatchOverrides);
                  }}
                  disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="id-match-override-submit-btn"
                >
                  {submitting ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Submitting…</> : <>Override & Submit</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Rendered inside the Autofill dialog — a small checklist of extracted fields
// the recruiter can tick before applying. Memo-free; local state is fine
// because this component unmounts whenever the dialog closes.
function AutofillRows({ rows, currentForm, onApply, onCancel }) {
  const [picked, setPicked] = useState(() => new Set(rows.map(r => r.key)));
  const toggle = (k) => setPicked(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <div className="space-y-2">
      {rows.map(r => {
        const existing = currentForm[r.key];
        const willOverwrite = !!existing && String(existing).trim() && String(existing).trim() !== String(r.val).trim();
        return (
          <label
            key={r.key}
            className={`flex items-start gap-3 p-2 rounded-md border cursor-pointer ${picked.has(r.key) ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'}`}
          >
            <Checkbox checked={picked.has(r.key)} onCheckedChange={() => toggle(r.key)} className="mt-0.5" data-testid={`autofill-row-${r.key}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{r.label}</div>
              <div className="text-sm text-gray-900 break-words">{r.val}</div>
              {willOverwrite && <div className="text-[11px] text-amber-700 mt-0.5">⚠ will overwrite your current value: <span className="font-medium">{existing}</span></div>}
            </div>
          </label>
        );
      })}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="autofill-cancel-btn">Cancel</Button>
        <Button
          type="button"
          onClick={() => onApply(Array.from(picked))}
          disabled={picked.size === 0}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="autofill-apply-btn"
        >
          Apply {picked.size} field{picked.size === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );
}


/* --------------------------------------------------------------------------
   PR-only conditional Q&A ladder
   Q1: "Do you have English test proof?" → yes → upload english_test_proof
                                          → no  → Q2
   Q2: "Do you have a 2-year Canadian transcript?" → yes → upload canadian_transcript_2yr
                                                    → no  → Q3
   Q3: "Would you like to give the Accuplacer test?" → yes / no  (recorded for admin)
   -------------------------------------------------------------------------- */
function StepTrail({ steps }) {
  return (
    <ol className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="step-trail">
      {steps.map((s, i) => {
        const state = s.done ? 'done' : s.active ? 'active' : 'idle';
        const dotClass =
          state === 'done'   ? 'bg-emerald-500 text-white ring-emerald-100'
          : state === 'active' ? 'bg-blue-600 text-white ring-blue-100 ring-4'
          :                      'bg-white text-gray-400 border border-gray-200';
        const labelClass =
          state === 'done'   ? 'text-emerald-700'
          : state === 'active' ? 'text-gray-900 font-semibold'
          :                      'text-gray-400';
        const barClass = state === 'done' ? 'bg-emerald-400' : state === 'active' ? 'bg-blue-300' : 'bg-gray-200';
        return (
          <li key={s.key} className="flex items-center gap-1 sm:gap-2 shrink-0" data-testid={`step-${s.key}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${dotClass}`}>
              {state === 'done' ? <CheckCircle size={12} weight="fill" /> : i + 1}
            </div>
            <span className={`text-[11px] uppercase tracking-widest ${labelClass}`}>{s.label}</span>
            {i < steps.length - 1 && <span className={`w-4 sm:w-8 h-0.5 rounded-full ${barClass}`} />}
          </li>
        );
      })}
    </ol>
  );
}

const SECTION_TONES = {
  blue:    { bar: 'bg-blue-500',    ring: 'ring-blue-100',    icon: 'bg-blue-100 text-blue-700',    pill: 'bg-blue-50 text-blue-700 border-blue-200' },
  indigo:  { bar: 'bg-indigo-500',  ring: 'ring-indigo-100',  icon: 'bg-indigo-100 text-indigo-700', pill: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  emerald: { bar: 'bg-emerald-500', ring: 'ring-emerald-100', icon: 'bg-emerald-100 text-emerald-700', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  violet:  { bar: 'bg-violet-500',  ring: 'ring-violet-100',  icon: 'bg-violet-100 text-violet-700', pill: 'bg-violet-50 text-violet-700 border-violet-200' },
  amber:   { bar: 'bg-amber-500',   ring: 'ring-amber-100',   icon: 'bg-amber-100 text-amber-700',   pill: 'bg-amber-50 text-amber-700 border-amber-200' },
  rose:    { bar: 'bg-rose-500',    ring: 'ring-rose-100',    icon: 'bg-rose-100 text-rose-700',     pill: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function SectionHeader({ icon: Icon, num, total = 6, title, subtitle, tone = 'blue', badge }) {
  const t = SECTION_TONES[tone] || SECTION_TONES.blue;
  return (
    <div className="flex items-start gap-4 mb-5">
      <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ring-4 ${t.ring} ${t.icon}`}>
        <Icon size={20} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-widest ${t.icon.replace('bg-', 'text-').replace('-100', '-700')} bg-transparent`}>
            Step {num} of {total}
          </span>
          {badge && <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${t.pill}`}>{badge}</span>}
        </div>
        <h2 className="text-xl font-bold text-gray-900 mt-0.5" style={{ fontFamily: "'Libre Baskerville', serif" }}>{title}</h2>
        {subtitle && <p className="text-[13px] text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}


function YesNoButtons({ value, onChange, testId }) {
  return (
    <div className="inline-flex gap-2 mt-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-4 h-9 rounded-md text-sm font-semibold border transition-colors ${value === true ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-300'}`}
        data-testid={`${testId}-yes`}
      >Yes</button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-4 h-9 rounded-md text-sm font-semibold border transition-colors ${value === false ? 'bg-gray-800 border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`}
        data-testid={`${testId}-no`}
      >No</button>
    </div>
  );
}

function PrConditionalLadder({ form, setForm, handleFileUpload, uploading, isSlotFilled, getSlotFile, displayFileName }) {
  const englishProofSlot = { key: 'english_test_proof', label: 'English Test Proof', doc_type: 'english_test_proof', matches: ['english_test_proof', 'english_test_certificate'] };
  const transcriptSlot = { key: 'canadian_transcript_2yr', label: 'Canadian 2-Year Transcript', doc_type: 'canadian_transcript_2yr', matches: ['canadian_transcript_2yr'] };
  const englishFilled = isSlotFilled(englishProofSlot);
  const englishFile = getSlotFile(englishProofSlot);
  const transcriptFilled = isSlotFilled(transcriptSlot);
  const transcriptFile = getSlotFile(transcriptSlot);
  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  return (
    <div className="mt-4 pt-4 border-t border-dashed border-blue-200 space-y-3" data-testid="pr-conditional-ladder">
      <p className="text-[11px] uppercase tracking-widest text-blue-800 font-bold">A few quick questions</p>

      {/* Q1: English test proof */}
      <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/40" data-testid="q-english-test-proof">
        <p className="text-sm font-semibold text-gray-800">Does the student have English test proof?</p>
        <YesNoButtons
          value={form.english_test_proof}
          onChange={(v) => set({ english_test_proof: v, canadian_transcript_2yr: v === true ? null : form.canadian_transcript_2yr, accuplacer_intent: v === true ? null : form.accuplacer_intent })}
          testId="english-proof"
        />
        {form.english_test_proof === true && (
          <div className={`flex items-center gap-3 p-3 rounded-lg border mt-3 ${englishFilled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${englishFilled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              <FileText size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">English Test Proof <span className="text-red-600">*</span></p>
              {englishFilled ? (
                <p className="text-xs text-gray-600 truncate">{displayFileName(englishFile)}</p>
              ) : (
                <p className="text-xs text-amber-700">Upload the certificate or score report</p>
              )}
            </div>
            <Label htmlFor="upload-english-test-proof" className="cursor-pointer shrink-0">
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${englishFilled ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-blue-700 text-white hover:bg-blue-800'}`}>
                <UploadSimple size={14} /> {englishFilled ? 'Replace' : 'Upload'}
              </div>
            </Label>
            <input
              id="upload-english-test-proof"
              type="file"
              className="hidden"
              onChange={(e) => handleFileUpload(e, 'english_test_proof')}
              disabled={uploading}
              data-testid="upload-english-test-proof-input"
            />
          </div>
        )}
      </div>

      {/* Q2: Canadian transcript — only asked if English test proof is "no" */}
      {form.english_test_proof === false && (
        <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/40" data-testid="q-canadian-transcript">
          <p className="text-sm font-semibold text-gray-800">Do they have a 2-year Canadian education transcript?</p>
          <YesNoButtons
            value={form.canadian_transcript_2yr}
            onChange={(v) => set({ canadian_transcript_2yr: v, accuplacer_intent: v === true ? null : form.accuplacer_intent })}
            testId="canadian-transcript"
          />
          {form.canadian_transcript_2yr === true && (
            <div className={`flex items-center gap-3 p-3 rounded-lg border mt-3 ${transcriptFilled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${transcriptFilled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                <FileText size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Canadian 2-Year Transcript <span className="text-red-600">*</span></p>
                {transcriptFilled ? (
                  <p className="text-xs text-gray-600 truncate">{transcriptFile?.original_filename}</p>
                ) : (
                  <p className="text-xs text-amber-700">Upload the transcript PDF or scan</p>
                )}
              </div>
              <Label htmlFor="upload-canadian-transcript" className="cursor-pointer shrink-0">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${transcriptFilled ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-blue-700 text-white hover:bg-blue-800'}`}>
                  <UploadSimple size={14} /> {transcriptFilled ? 'Replace' : 'Upload'}
                </div>
              </Label>
              <input
                id="upload-canadian-transcript"
                type="file"
                className="hidden"
                onChange={(e) => handleFileUpload(e, 'canadian_transcript_2yr')}
                disabled={uploading}
                data-testid="upload-canadian-transcript-input"
              />
            </div>
          )}
        </div>
      )}

      {/* Q3: Accuplacer intent — only asked if Canadian transcript is "no" */}
      {form.english_test_proof === false && form.canadian_transcript_2yr === false && (
        <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/40" data-testid="q-accuplacer">
          <p className="text-sm font-semibold text-gray-800">Would they like to give the Accuplacer test?</p>
          <p className="text-[11px] text-gray-500 mt-0.5">We&rsquo;ll record their intent so admin can follow up.</p>
          <YesNoButtons value={form.accuplacer_intent} onChange={(v) => set({ accuplacer_intent: v })} testId="accuplacer" />
        </div>
      )}
    </div>
  );
}
